import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiAccountCircleFill, RiLogoutCircleLine } from 'react-icons/ri';
import { FiLock, FiBell, FiCreditCard, FiShoppingCart, FiChevronDown, FiChevronRight, FiCoffee, FiDownload } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useFoodCart } from '../../contexts/FoodCartContext';
import { notificationsService } from '../../services';
import type { Notification } from '../../types';

// Route-to-i18n key mapping for auto page titles
const ROUTE_TITLE_KEYS: Record<string, string> = {
  '/dashboard': 'nav.dashboard',
  '/payments': 'nav.payments',
  '/payments/result': 'nav.paymentResult',
  '/transactions': 'nav.transactions',
  '/wallet': 'nav.wallet',
  '/banking': 'nav.banking',
  '/banking/transfer': 'nav.bankingTransfer',
  '/banking/bills': 'nav.bankingBills',
  '/insights': 'nav.insights',
  '/profile': 'nav.profile',
  '/ecommerce': 'nav.ecommerce',
  '/ecommerce/orders': 'nav.orders',
  '/ecommerce/checkout': 'nav.checkout',
  '/eventos': 'nav.events',
  '/doacoes': 'nav.donations',
  '/vouchers': 'nav.vouchers',
  '/academia': 'nav.academia',
  '/hub': 'nav.hub',
  '/food': 'nav.food',
  '/food/cart': 'nav.foodCart',
  '/food/orders': 'nav.foodOrders',
  '/portal': 'nav.portal',
  '/portal/dashboard': 'nav.portalDashboard',
  '/portal/products': 'nav.portalProducts',
  '/portal/services': 'nav.portalServices',
  '/portal/payments': 'nav.portalSales',
  '/portal/categories': 'nav.portalCategories',
  '/portal/orders': 'nav.portalOrders',
  '/portal/inventory': 'nav.portalInventory',
  '/portal/reports': 'nav.portalReports',
  '/portal/events': 'nav.portalEvents',
  '/portal/donations': 'nav.portalDonations',
  '/portal/variations': 'nav.portalVariations',
  '/portal/announcements': 'nav.portalAnnouncements',
};

// Get breadcrumbs from pathname
function getBreadcrumbs(pathname: string, t: (key: string) => string): { label: string; path?: string }[] {
  const crumbs: { label: string; path?: string }[] = [];
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length <= 1) return [];

  let currentPath = '';
  for (let i = 0; i < segments.length; i++) {
    currentPath += `/${segments[i]}`;
    const titleKey = ROUTE_TITLE_KEYS[currentPath];
    if (titleKey) {
      const title = t(titleKey);
      if (i < segments.length - 1) {
        crumbs.push({ label: title, path: currentPath });
      } else {
        crumbs.push({ label: title });
      }
    }
  }

  return crumbs.length > 1 ? crumbs : [];
}

// Resolve page title from current route
function getPageTitle(pathname: string, t: (key: string) => string): string {
  // Check exact match first
  if (ROUTE_TITLE_KEYS[pathname]) return t(ROUTE_TITLE_KEYS[pathname]);

  // Check pattern matches (for dynamic routes like /payments/:id/:name)
  for (const [route, titleKey] of Object.entries(ROUTE_TITLE_KEYS)) {
    if (pathname.startsWith(route) && route !== '/') return t(titleKey);
  }

  // Check sessionStorage as fallback (for pages that set it dynamically, like store detail)
  return sessionStorage.getItem('pageTitle') || t('nav.dashboard');
}

// Icon Button Component with pastel colors
interface IconButtonProps {
  icon: React.ReactNode;
  bgColor: string;
  onClick?: () => void;
  badge?: number;
  ariaLabel: string;
}

const IconButton: React.FC<IconButtonProps> = ({ icon, bgColor, onClick, badge, ariaLabel }) => (
  <button
    onClick={onClick}
    className={`relative w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity`}
    aria-label={ariaLabel}
  >
    {icon}
    {badge !== undefined && badge > 0 && (
      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
        {badge > 9 ? '9+' : badge}
      </span>
    )}
  </button>
);

const HeaderUser: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { cliente, user, logout } = useAuth();
  const { itemCount } = useCart();
  const { itemCount: foodItemCount, restaurant: foodRestaurant } = useFoodCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load notifications
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const data = await notificationsService.getNotifications();
        setNotifications(data.slice(0, 5));
        setUnreadCount(data.filter((n) => !n.lida).length);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    };

    loadNotifications();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleGoToProfile = () => {
    setMenuOpen(false);
    navigate('/profile');
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, lida: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return t('common.justNow');
    if (hours < 24) return t('common.hoursAgo', { count: hours });
    if (days < 7) return t('common.daysAgo', { count: days });
    return date.toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' });
  };

  const pageTitle = getPageTitle(location.pathname, t);
  const breadcrumbs = getBreadcrumbs(location.pathname, t);

  const avatarUrl =
    cliente?.AVATAR && cliente?.MIME_TYPE
      ? `data:${cliente.MIME_TYPE};base64,${cliente.AVATAR}`
      : null;

  return (
    <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-pagali-gray-200">
      {/* Page Title + Breadcrumbs */}
      <div>
        {breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-1.5 mb-0.5">
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && <FiChevronRight className="text-pagali-gray-400 text-xs" />}
                {crumb.path ? (
                  <button
                    onClick={() => navigate(crumb.path!)}
                    className="text-xs text-pagali-gray-500 hover:text-pagali-primary transition-colors"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="text-xs text-pagali-gray-400">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}
        <h1 className="text-xl font-bold text-pagali-gray-800">{pageTitle}</h1>
      </div>

      {/* Right Side - Icons and User */}
      <div className="flex items-center gap-3">
        {/* Security Icon - Emerald */}
        <IconButton
          icon={<FiLock className="text-lg text-emerald-600" />}
          bgColor="bg-emerald-100"
          onClick={() => navigate('/profile')}
          ariaLabel={t('nav.security')}
        />

        {/* Notifications Icon - Blue */}
        <div className="relative" ref={notificationsRef}>
          <IconButton
            icon={<FiBell className="text-lg text-blue-600" />}
            bgColor="bg-blue-100"
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            badge={unreadCount}
            ariaLabel={t('nav.notifications')}
          />

          {/* Notifications Dropdown */}
          {notificationsOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-pagali-gray-200 z-50 animate-fade-in">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-pagali-gray-100">
                <h3 className="font-semibold text-pagali-gray-800">{t('notifications.title')}</h3>
                {unreadCount > 0 && (
                  <span className="text-xs text-pagali-primary font-medium">
                    {t('notifications.unread', { count: unreadCount })}
                  </span>
                )}
              </div>

              {/* List */}
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="py-8 text-center text-pagali-gray-500">
                    <FiBell className="text-4xl mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('notifications.empty')}</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => handleMarkAsRead(notification.id)}
                      className={`w-full text-left px-4 py-3 border-b border-pagali-gray-50 hover:bg-pagali-gray-50 transition-colors ${
                        !notification.lida ? 'bg-pagali-primary/5' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {!notification.lida && (
                          <span className="w-2 h-2 mt-2 rounded-full bg-pagali-primary flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-pagali-gray-800 truncate">
                            {notification.titulo}
                          </p>
                          <p className="text-xs text-pagali-gray-500 mt-1 line-clamp-2">
                            {notification.mensagem}
                          </p>
                          <p className="text-xs text-pagali-gray-400 mt-1">
                            {formatDate(notification.data)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="px-4 py-3 border-t border-pagali-gray-100 text-center">
                  <button
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('/notifications');
                    }}
                    className="text-sm text-pagali-primary font-medium hover:underline"
                  >
                    {t('notifications.viewAll')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Wallet Icon - Purple */}
        <IconButton
          icon={<FiCreditCard className="text-lg text-purple-600" />}
          bgColor="bg-purple-100"
          onClick={() => navigate('/wallet')}
          ariaLabel={t('nav.wallet')}
        />

        {/* Food Cart - Amber (only when food items exist) */}
        {foodItemCount > 0 && foodRestaurant && (
          <IconButton
            icon={<FiCoffee className="text-lg text-amber-600" />}
            bgColor="bg-amber-100"
            onClick={() => navigate('/food/cart')}
            badge={foodItemCount}
            ariaLabel={t('nav.foodCart')}
          />
        )}

        {/* Downloads - Indigo */}
        <IconButton
          icon={<FiDownload className="text-lg text-indigo-600" />}
          bgColor="bg-indigo-100"
          onClick={() => navigate('/meus-downloads')}
          ariaLabel="Meus Downloads"
        />

        {/* Shop Cart - Rose */}
        <IconButton
          icon={<FiShoppingCart className="text-lg text-rose-600" />}
          bgColor="bg-rose-100"
          onClick={() => navigate(itemCount > 0 ? '/ecommerce/checkout' : '/ecommerce')}
          badge={itemCount}
          ariaLabel={t('nav.shopCart')}
        />

        {/* User Menu */}
        <div className="relative ml-2" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-3 p-2 rounded-xl hover:bg-pagali-gray-50 transition-colors"
          >
            {/* Avatar */}
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={cliente?.NOME || 'User'}
                className="w-10 h-10 rounded-lg object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-pagali-gray-200 flex items-center justify-center text-pagali-gray-600 font-medium">
                {(cliente?.NOME || user?.username || 'U').charAt(0).toUpperCase()}
              </div>
            )}

            {/* Name */}
            <span className="hidden md:block text-sm font-medium text-pagali-gray-700">
              {cliente?.NOME || user?.username || t('common.user')}
            </span>

            {/* Chevron */}
            <FiChevronDown
              className={`w-4 h-4 text-pagali-gray-400 transition-transform ${
                menuOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-pagali-gray-200 py-2 z-50 animate-fade-in">
              <button
                onClick={handleGoToProfile}
                className="flex items-center gap-3 w-full px-4 py-3 text-left text-pagali-gray-700 hover:bg-pagali-gray-50 transition-colors"
              >
                <RiAccountCircleFill className="text-xl text-pagali-gray-400" />
                <span>{t('common.viewProfile')}</span>
              </button>

              <hr className="my-2 border-pagali-gray-100" />

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-3 text-left text-pagali-error hover:bg-pagali-error-light transition-colors"
              >
                <RiLogoutCircleLine className="text-xl" />
                <span>{t('common.endSession')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default HeaderUser;
